import { useId } from "react";
import { Phone } from "lucide-react";

const DIAL_LABELS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
const CORD_COILS = Array.from({ length: 12 });

export function TelefonkaHeroPhone({
  className = "",
  decorative = true,
  title = "Telefonka retró tárcsás telefon",
}) {
  const id = useId().replace(/:/g, "");
  const titleId = `${id}-title`;
  const shadowId = `${id}-shadow`;
  const bodyGradientId = `${id}-body`;
  const handsetGradientId = `${id}-handset`;
  const highlightGradientId = `${id}-highlight`;

  return (
    <svg
      viewBox="0 0 640 540"
      xmlns="http://www.w3.org/2000/svg"
      className={`block h-auto w-full max-w-full ${className}`}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? "true" : undefined}
      aria-labelledby={decorative ? undefined : titleId}
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
    >
      {!decorative && <title id={titleId}>{title}</title>}

      <defs>
        <filter id={shadowId} x="-20%" y="-20%" width="150%" height="170%">
          <feDropShadow
            dx="0"
            dy="18"
            stdDeviation="15"
            floodColor="var(--ink)"
            floodOpacity="0.16"
          />
        </filter>
        <linearGradient id={bodyGradientId} x1="0.12" y1="0" x2="0.86" y2="1">
          <stop offset="0" stopColor="var(--burgundy-soft)" />
          <stop offset="0.48" stopColor="var(--burgundy)" />
          <stop offset="1" stopColor="var(--burgundy-dark)" />
        </linearGradient>
        <linearGradient id={handsetGradientId} x1="0" y1="0" x2="1" y2="0.85">
          <stop offset="0" stopColor="var(--burgundy-soft)" />
          <stop offset="0.5" stopColor="var(--burgundy)" />
          <stop offset="1" stopColor="var(--burgundy-dark)" />
        </linearGradient>
        <linearGradient id={highlightGradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="var(--beige-100)" stopOpacity="0.08" />
          <stop offset="0.5" stopColor="var(--beige-100)" stopOpacity="0.5" />
          <stop offset="1" stopColor="var(--beige-100)" stopOpacity="0.08" />
        </linearGradient>
      </defs>

      <g filter={`url(#${shadowId})`}>
        <path
          d="M166 250C173 216 203 194 240 190H400C437 194 467 216 474 250L506 402H134L166 250Z"
          fill={`url(#${bodyGradientId})`}
          stroke="var(--burgundy-dark)"
          strokeWidth="7"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        <path
          d="M153 368H487L513 423C519 436 510 451 495 451H145C130 451 121 436 127 423L153 368Z"
          fill="var(--burgundy-dark)"
          stroke="var(--burgundy-dark)"
          strokeWidth="7"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M126 421H514C531 421 544 435 544 452V472C544 486 533 497 519 497H121C107 497 96 486 96 472V452C96 435 109 421 126 421Z"
          fill={`url(#${bodyGradientId})`}
          stroke="var(--burgundy-dark)"
          strokeWidth="8"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M118 445H522"
          fill="none"
          stroke="var(--beige-100)"
          strokeOpacity="0.22"
          strokeWidth="8"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M149 384H491"
          fill="none"
          stroke="var(--beige-100)"
          strokeOpacity="0.18"
          strokeWidth="7"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        <path
          d="M200 210V249M440 210V249"
          fill="none"
          stroke="var(--burgundy-dark)"
          strokeWidth="22"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        <path
          d="M87 134C98 85 147 52 220 48H420C493 52 542 85 553 134L522 172C501 148 473 137 427 135H213C167 137 139 148 118 172L87 134Z"
          fill={`url(#${handsetGradientId})`}
          stroke="var(--burgundy-dark)"
          strokeWidth="8"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M197 65C210 91 218 118 218 145H422C422 118 430 91 443 65"
          fill="var(--burgundy-dark)"
          fillOpacity="0.55"
          stroke="var(--burgundy-dark)"
          strokeWidth="5"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M136 124C158 91 190 78 229 76H411C450 78 482 91 504 124"
          fill="none"
          stroke={`url(#${highlightGradientId})`}
          strokeWidth="11"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        <path
          d="M86 130C65 140 55 161 60 184C65 210 88 228 115 226C139 224 158 210 166 189L118 172L86 130Z"
          fill={`url(#${handsetGradientId})`}
          stroke="var(--burgundy-dark)"
          strokeWidth="8"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M554 130C575 140 585 161 580 184C575 210 552 228 525 226C501 224 482 210 474 189L522 172L554 130Z"
          fill={`url(#${handsetGradientId})`}
          stroke="var(--burgundy-dark)"
          strokeWidth="8"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M70 170C91 182 117 185 144 176M570 170C549 182 523 185 496 176"
          fill="none"
          stroke="var(--beige-100)"
          strokeOpacity="0.42"
          strokeWidth="8"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        <path
          d="M77 182C48 204 43 236 52 264C61 292 60 323 48 349C37 373 42 400 62 414C79 426 103 424 118 410"
          fill="none"
          stroke="var(--burgundy-dark)"
          strokeWidth="9"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {CORD_COILS.map((_, index) => {
          const cy = 252 + index * 13;
          const cx = 67 + Math.sin(index * 0.8) * 7;
          const rotation = index % 2 === 0 ? -8 : 8;

          return (
            <ellipse
              key={index}
              cx={cx}
              cy={cy}
              rx="19"
              ry="7"
              fill="none"
              stroke="var(--burgundy-dark)"
              strokeWidth="7"
              strokeLinecap="round"
              transform={`rotate(${rotation} ${cx} ${cy})`}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}

        <circle
          cx="320"
          cy="311"
          r="116"
          fill="var(--burgundy-dark)"
          stroke="var(--burgundy-dark)"
          strokeWidth="8"
          vectorEffect="non-scaling-stroke"
        />
        <circle
          cx="320"
          cy="311"
          r="103"
          fill="var(--beige-100)"
          stroke="var(--burgundy-soft)"
          strokeWidth="7"
          vectorEffect="non-scaling-stroke"
        />
        <circle
          cx="320"
          cy="311"
          r="82"
          fill="var(--burgundy)"
          stroke="var(--burgundy-dark)"
          strokeWidth="6"
          vectorEffect="non-scaling-stroke"
        />

        {DIAL_LABELS.map((label, index) => {
          const angle = (index * 32 - 144) * (Math.PI / 180);
          const holeX = 320 + Math.cos(angle) * 78;
          const holeY = 311 + Math.sin(angle) * 78;

          return (
            <g key={label}>
              <circle
                cx={holeX}
                cy={holeY}
                r="17"
                fill="var(--beige-100)"
                stroke="var(--burgundy-dark)"
                strokeWidth="4"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={holeX}
                y={holeY + 5}
                textAnchor="middle"
                fontSize="14"
                fontWeight="700"
                fontFamily="Inter, system-ui, sans-serif"
                fill="var(--burgundy-dark)"
              >
                {label}
              </text>
            </g>
          );
        })}

        <circle
          cx="320"
          cy="311"
          r="53"
          fill="var(--beige-50)"
          stroke="var(--burgundy-dark)"
          strokeWidth="6"
          vectorEffect="non-scaling-stroke"
        />
        <circle
          cx="320"
          cy="311"
          r="21"
          fill="var(--beige-100)"
          stroke="var(--burgundy)"
          strokeWidth="6"
          vectorEffect="non-scaling-stroke"
        />
        <circle cx="320" cy="311" r="8" fill="var(--burgundy-dark)" />

        <path
          d="M390 364L419 381L398 401Z"
          fill="var(--beige-100)"
          stroke="var(--burgundy-dark)"
          strokeWidth="5"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        <path
          d="M191 262C210 231 239 216 274 212H366C401 216 430 231 449 262"
          fill="none"
          stroke="var(--beige-100)"
          strokeOpacity="0.2"
          strokeWidth="9"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </svg>
  );
}

export default function HeroPhone({ t }) {
  return (
    <a
      href="#booking"
      data-testid="hero-phone"
      aria-label={t.hero.phoneAria}
      className="relative mx-auto block w-full max-w-[30rem] overflow-visible focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(110,30,43,0.2)] sm:max-w-[34rem] lg:max-w-[36rem] tk-fade-in tk-phone-link"
    >
      <TelefonkaHeroPhone className="tk-phone-shake mx-auto max-h-[31rem] object-contain sm:max-h-[34rem] lg:max-h-[38rem]" />

      <div className="absolute bottom-1 left-0 sm:bottom-4 sm:left-3 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 border border-[rgba(110,30,43,0.08)]">
        <span className="w-9 h-9 rounded-full bg-[var(--beige-100)] flex items-center justify-center text-[var(--burgundy)]">
          <Phone size={15} aria-hidden="true" />
        </span>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--burgundy)]">
            {t.hero.badgeKicker}
          </p>
          <p className="text-sm font-medium text-[var(--ink)]">{t.hero.badgeText}</p>
        </div>
      </div>

      <span
        aria-hidden="true"
        className="absolute right-3 top-2 font-serif text-lg italic text-[var(--burgundy)] opacity-70 sm:right-7 sm:top-6"
      >
        ♥
      </span>
    </a>
  );
}
